import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'danger'

interface ButtonBaseProps {
  variant?: Variant
  children: ReactNode
  className?: string
  size?: 'sm' | 'md'
}

interface ButtonAsLink extends ButtonBaseProps {
  href: string
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete'
  onClick?: never
  type?: never
  disabled?: never
}

interface ButtonAsButton extends ButtonBaseProps {
  href?: never
  method?: never
  onClick?: () => void
  type?: 'button' | 'submit'
  disabled?: boolean
}

type ButtonProps = ButtonAsLink | ButtonAsButton

const variantClasses: Record<Variant, string> = {
  primary: 'bg-botanical text-white hover:bg-botanical/90',
  secondary: 'border border-frame text-ink hover:bg-paper-dark',
  danger: 'bg-danger text-white hover:bg-danger/90',
}

export function Button({
  variant = 'primary',
  children,
  className = '',
  size = 'md',
  ...props
}: ButtonProps) {
  const sizeClasses = size === 'sm' ? 'px-sm py-xs text-xs' : 'px-md py-sm text-sm'
  const base = `inline-flex items-center justify-center font-label tracking-wide transition-colors ${variantClasses[variant]} ${sizeClasses} ${className}`

  if ('href' in props && props.href) {
    return (
      <Link
        href={props.href}
        method={props.method}
        className={base}
      >
        {children}
      </Link>
    )
  }

  const { onClick, type = 'button', disabled } = props as ButtonAsButton
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  )
}
